// components/GoogleMap/LocationMap.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { loadGoogleMaps } from 'utils/googleMaps';

const LocationMap = ({
  latitude,
  longitude,
  onLocationChange,
  height = '300px',
  zoom = 15,
}) => {
  const containerRef = useRef(null);
  const mapDivRef = useRef(null);
  const mapInstance = useRef(null);
  const markerInstance = useRef(null);
  const geocoderInstance = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [error, setError] = useState(null);

  // Add marker to map
  const addMarker = useCallback((lat, lng) => {
    if (!mapInstance.current || !window.google) return;

    if (markerInstance.current) {
      markerInstance.current.setMap(null);
      markerInstance.current = null;
    }

    const position = { lat, lng };
    markerInstance.current = new window.google.maps.Marker({
      position,
      map: mapInstance.current,
      draggable: true,
      animation: window.google.maps.Animation.DROP,
    });

    markerInstance.current.addListener('dragend', () => {
      const pos = markerInstance.current.getPosition();
      handleLocationSelect(pos.lat(), pos.lng());
    });

    mapInstance.current.setCenter(position);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle location selection with reverse geocoding
  const handleLocationSelect = useCallback(
    (lat, lng) => {
      if (!geocoderInstance.current) return;

      geocoderInstance.current.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const result = results[0];
          const addressData = {
            latitude: lat,
            longitude: lng,
            formattedAddress: result.formatted_address,
            line1: '',
            city: '',
            state: '',
            postalCode: '',
          };

          result.address_components.forEach((component) => {
            const types = component.types;
            if (types.includes('street_number')) {
              addressData.line1 = component.long_name + ' ' + addressData.line1;
            }
            if (types.includes('route')) {
              addressData.line1 += component.long_name;
            }
            if (types.includes('locality')) addressData.city = component.long_name;
            if (types.includes('administrative_area_level_1')) addressData.state = component.short_name;
            if (types.includes('postal_code')) addressData.postalCode = component.long_name;
          });

          addressData.line1 = addressData.line1.trim();

          onLocationChange?.(addressData);
          addMarker(lat, lng);
        }
      });
    },
    [onLocationChange, addMarker]
  );

  // Initialize map once
  useEffect(() => {
    let isMounted = true;

    const initMap = async () => {
      try {
        // 1. Wait for Google Maps to load (which now fetches the API key from server internally)
        const google = await loadGoogleMaps();
        
        if (!isMounted || !containerRef.current || mapInstance.current) return;

        // Check if Map class is available (it will be, as we loaded 'maps' library)
        if (!google.maps.Map) {
          throw new Error('Google Maps Map class not loaded');
        }

        geocoderInstance.current = new google.maps.Geocoder();

        const innerDiv = document.createElement('div');
        innerDiv.style.width = '100%';
        innerDiv.style.height = '100%';
        containerRef.current.appendChild(innerDiv);
        mapDivRef.current = innerDiv;

        const center = {
          lat: latitude || 40.7128,
          lng: longitude || -74.006,
        };

        mapInstance.current = new google.maps.Map(innerDiv, {
          center,
          zoom,
          mapTypeControl: false,
          fullscreenControl: false,
          streetViewControl: false,
          zoomControl: true,
          styles: [{ featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }],
        });

        mapInstance.current.addListener('click', (e) => {
          handleLocationSelect(e.latLng.lat(), e.latLng.lng());
        });

        if (latitude && longitude) {
          addMarker(latitude, longitude);
        }

        if (isMounted) setMapLoaded(true);
      } catch (err) {
        console.error('Map initialization error:', err);
        if (isMounted) setError('Failed to load Google Maps. Please check your API key or network connection.');
      }
    };

    initMap();

    return () => {
      isMounted = false;

      if (markerInstance.current) {
        markerInstance.current.setMap(null);
        markerInstance.current = null;
      }
      if (mapInstance.current && window.google) {
        window.google.maps.event.clearInstanceListeners(mapInstance.current);
      }
      mapInstance.current = null;
      geocoderInstance.current = null;

      if (mapDivRef.current && mapDivRef.current.parentNode) {
        mapDivRef.current.parentNode.removeChild(mapDivRef.current);
      }
      mapDivRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update marker + center when coordinates change from outside
  useEffect(() => {
    if (mapInstance.current && latitude && longitude) {
      addMarker(latitude, longitude);
      mapInstance.current.setCenter({ lat: latitude, lng: longitude });
    }
  }, [latitude, longitude, addMarker]);

  return (
    <div
      style={{ height, width: '100%' }}
      className="rounded-lg border border-gray-200 overflow-hidden relative"
    >
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />

      {!mapLoaded && !error && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
          <div className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 border-4 border-[#4F46E5] border-t-transparent rounded-full animate-spin"></div>
            <span className="text-sm text-gray-500">Loading map...</span>
          </div>
        </div>
      )}

      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-red-50">
          <div className="text-center text-red-600 px-4">
            <p className="font-medium">⚠️ Map Error</p>
            <p className="text-sm">{error}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationMap;